import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail, Sparkles, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Contact({ selectedService, setSelectedService }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'Website Development',
    'Full Stack Applications',
    'SaaS Platforms',
    'Salon Management Systems',
    'Admin Dashboards',
    'Appointment Booking Systems',
    'Cloud & DevOps Deployments',
    'AI & Chat Agent Integrations'
  ];

  // Sync selected service when clicked from the services card grid
  useEffect(() => {
    if (selectedService) {
      setFormData((prev) => ({ ...prev, service: selectedService }));
    }
  }, [selectedService]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Sync back to parent if service dropdown is manually changed
    if (name === 'service' && setSelectedService) {
      setSelectedService(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await fetch('https://script.google.com/macros/s/AKfycbzznq1TlvQ85NB7Mi3JpravTCBRUZ0cZ6oURrgZfN6I04_SO_pzsmodbr8Z3EQyLwav5Q/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString()
        })
      });

      toast.success('Message received successfully! Our engineers will review and contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      if (setSelectedService) {
        setSelectedService('');
      }
    } catch (error) {
      toast.error('Could not submit details. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative bg-[#F8F5F0]" data-testid="contact-section">
      {/* Background Blobs */}
      <div className="absolute bottom-1/4 right-1/4 glowing-blob blob-beige opacity-[0.05]" />
      <div className="absolute top-1/4 left-1/4 glowing-blob blob-gold opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#8B6B4A]/5 border border-[#8B6B4A]/10 rounded-full px-4 py-1.5 backdrop-blur-md mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#C8A97E]" />
            <span className="text-xs font-semibold tracking-wider uppercase text-[#8B6B4A] font-poppins">
              Start Your Scale Journey
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight" data-testid="contact-heading">
            Let's Co-Engineer Your Product
          </h2>
          <p className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4" data-testid="contact-description">
            Fill out the form below, and we will schedule an architectural call with our lead development team.
          </p>
        </div>

        {/* Form and Info Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-stretch">
          
          {/* Column 1: Info & Pitch */}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-8" data-testid="contact-info">
            
            <div className="bg-white/40 border border-[#8B6B4A]/10 p-8 rounded-3xl backdrop-blur-md space-y-8">
              <h3 className="text-2xl font-bold font-space text-[#3E2F24]">Why Code Credence?</h3>
              
              <div className="space-y-4">
                {[
                  '1-on-1 calls directly with founders / senior developers.',
                  'Detailed technical roadmap and database schemas proposed.',
                  'Fixed milestones with exact launch scheduling.'
                ].map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-4.5 h-4.5 text-[#8B6B4A] mt-0.5 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-poppins text-[#6B625B] leading-relaxed">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct Contact Cards */}
            <div className="space-y-4">
              
              <div className="flex items-center gap-4 bg-white/40 border border-[#8B6B4A]/10 p-4 rounded-2xl hover:border-[#8B6B4A]/30 transition-colors shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#8B6B4A]/5 text-[#8B6B4A] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-[#6B625B] font-poppins">Office HQ</div>
                  <div className="text-xs sm:text-sm text-[#3E2F24] font-medium font-poppins" data-testid="contact-address">
                    Airoli Sec 2, Navi Mumbai, India
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white/40 border border-[#8B6B4A]/10 p-4 rounded-2xl hover:border-[#8B6B4A]/30 transition-colors shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#C8A97E]/5 text-[#C8A97E] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-[#6B625B] font-poppins">Technical Sales</div>
                  <a href="mailto:info@codecredence.com" className="text-xs sm:text-sm text-[#3E2F24] hover:text-[#8B6B4A] font-medium font-poppins transition-colors" data-testid="contact-email">
                    info@codecredence.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white/40 border border-[#8B6B4A]/10 p-4 rounded-2xl hover:border-[#8B6B4A]/30 transition-colors shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#8B6B4A]/5 text-[#8B6B4A] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-[#6B625B] font-poppins">Direct Line</div>
                  <div className="text-xs sm:text-sm text-[#3E2F24] font-medium font-poppins" data-testid="contact-phone">
                    +91 8083483783
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* Column 2: Form */}
          <div className="lg:col-span-7" data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="bg-white/40 border border-[#8B6B4A]/10 p-8 md:p-10 rounded-3xl backdrop-blur-md space-y-6 shadow-sm">
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold text-[#6B625B] uppercase tracking-wider mb-2 font-poppins">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/70 border border-[#8B6B4A]/10 focus:border-[#8B6B4A]/40 focus:ring-1 focus:ring-[#8B6B4A]/20 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#3E2F24] placeholder-slate-400 transition-all outline-none"
                    placeholder="John Doe"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-[#6B625B] uppercase tracking-wider mb-2 font-poppins">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/70 border border-[#8B6B4A]/10 focus:border-[#8B6B4A]/40 focus:ring-1 focus:ring-[#8B6B4A]/20 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#3E2F24] placeholder-slate-400 transition-all outline-none"
                    placeholder="john@example.com"
                    data-testid="contact-form-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-[#6B625B] uppercase tracking-wider mb-2 font-poppins">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white/70 border border-[#8B6B4A]/10 focus:border-[#8B6B4A]/40 focus:ring-1 focus:ring-[#8B6B4A]/20 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#3E2F24] placeholder-slate-400 transition-all outline-none"
                    placeholder="+91 99999 99999"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-xs font-semibold text-[#6B625B] uppercase tracking-wider mb-2 font-poppins">
                    Project Service interested in *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/70 border border-[#8B6B4A]/10 focus:border-[#8B6B4A]/40 focus:ring-1 focus:ring-[#8B6B4A]/20 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#3E2F24] transition-all outline-none font-poppins"
                    data-testid="contact-form-service"
                  >
                    <option value="" className="bg-[#F8F5F0]">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service} className="bg-[#F8F5F0] text-[#3E2F24]">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-[#6B625B] uppercase tracking-wider mb-2 font-poppins">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-white/70 border border-[#8B6B4A]/10 focus:border-[#8B6B4A]/40 focus:ring-1 focus:ring-[#8B6B4A]/20 rounded-xl px-4 py-3 text-xs sm:text-sm text-[#3E2F24] placeholder-slate-400 transition-all outline-none resize-none"
                  placeholder="Explain your software or website requirements..."
                  data-testid="contact-form-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full relative group overflow-hidden bg-[#3E2F24] hover:bg-[#8B6B4A] text-white rounded-xl py-4 font-bold font-poppins transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-form-submit"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {isSubmitting ? 'Sending Brief...' : 'Submit Inquiry'}
                  <Send className="w-4 h-4" />
                </span>
                <span className="absolute inset-0 bg-[#8B6B4A] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </button>

            </form>
          </div>

        </div>

      </div>
    </section>
  );
}
